/**
 * Cliente HTTP para el API.
 * Usa cookies para auth (httpOnly, secure en prod).
 */

import type { QueryClient } from '@tanstack/react-query'

const API_BASE = import.meta.env.VITE_API_BASE ?? ''

/**
 * Error tipado. Permite distinguir 401 (sesión expirada), 403 (scope
 * faltante), 420 (ESI rate-limited) y 5xx (server) en los call sites.
 * Antes todo era `Error(string)` y las pages no podían diferenciar.
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly body: unknown,
  ) {
    super(`${status}: ${code}`)
  }
}

/**
 * Bind del QueryClient para poder invalidar queries desde el HTTP layer.
 * Se llama desde main.tsx después de crear el client. Si no se bindea,
 * las operaciones que dependen de él son no-ops.
 */
let _qc: QueryClient | null = null
export function bindQueryClient(qc: QueryClient) {
  _qc = qc
}

async function http<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: 'include',  // enviar cookies
    headers: {
      'Content-Type': 'application/json',
      ...init.headers,
    },
  })

  // 401 → sesión expirada. Invalidar ['me'] para forzar redirect a login.
  if (res.status === 401) {
    _qc?.setQueryData(['me'], { authenticated: false })
    if (typeof location !== 'undefined' && !location.pathname.startsWith('/login')) {
      location.assign('/login?expired=1')
    }
    throw new ApiError(401, 'unauthorized', null)
  }

  if (!res.ok) {
    const body = await res.text().catch(() => '')
    let code = 'unknown'
    try {
      code = (JSON.parse(body) as { error?: string }).error ?? 'unknown'
    } catch {
      /* non-JSON body, leave code as 'unknown' */
    }
    throw new ApiError(res.status, code, body)
  }
  return res.json() as Promise<T>
}

/** Response shape de /api/blueprint-minerals. */
export interface BlueprintMineralsResponse {
  blueprint: { type_id: number; name: string }
  runs: number
  refining_efficiency: number
  materials: Array<{
    type_id: number
    name: string
    quantity_per_run: number
    quantity_total: number
    jita_buy: number | null
    unit_cost_isk: number
    total_cost_isk: number
    ores: Array<{
      type_id: number
      name: string
      yield_per_100: number
      qty_to_mine: number
    }>
  }>
  /**
   * Ores que cubren múltiples materiales del BP a la vez (batch yield).
   * Orden: # materiales cubiertos (desc), luego yield total (desc).
   * Top 10.
   */
  mining_plan: Array<{
    type_id: number
    name: string
    /** Cantidad de ore a minar para cubrir el material más exigente. */
    qty_to_mine: number
    yields: Array<{
      material_id: number
      material_name: string
      yield_per_100: number
      /** Unidades que se obtienen minando `qty_to_mine` (con efficiency). */
      units_gained: number
      /** Unidades que el BP necesita. */
      units_needed: number
      /** Porcentaje de cobertura (puede ser >100% si la ore produce en exceso). */
      pct_covered: number
    }>
  }>
  total_jita_buy: number
  /** Presente cuando el type no tiene BP (ej: minerales raw). UI lo muestra
   *  como info banner, NO como error. */
  note?: string
}

/**
 * Multi-char support — fila del switcher. Una misma `dashboard_user`
 * puede tener 1..N chars (login en este browser linkea al dashboard_user
 * del cookie actual).
 */
export interface DashboardCharacter {
  id: number
  name: string
  portrait: string | null
  corporation_id: number
  alliance_id: number | null
  last_login_at: string
}

export const api = {
  me: () => http<{
    authenticated: boolean
    character?: {
      id: number
      name: string
      portrait: string | null
      corporation_id: number
      alliance_id: number | null
    }
    /** UUID de la cuenta — agrupa los chars linkeados. */
    dashboard_user_id?: string
    /** ID del char activo en esta sesión. */
    active_character_id?: number
    /** Todos los chars linkeados del dashboard_user (ordered last_login_at DESC). */
    characters?: DashboardCharacter[]
    roles_bitmask?: number
  }>('/auth/me'),

  loginUrl: () => `${API_BASE}/auth/login`,

  logout: () => http<{ ok: boolean }>('/auth/logout', { method: 'POST' }),

  /**
   * Lista chars linkeados — para refrescar el switcher sin re-fetchear
   * el resto de /me. Devuelve siempre el active_character_id.
   */
  myCharacters: () =>
    http<{ active_character_id: number; characters: DashboardCharacter[] }>(
      '/auth/characters'
    ),

  /**
   * Switch active char — server-side. Cambia `sessions.character_id` a
   * `id` (validando que `id` pertenezca al mismo `dashboard_user`).
   * Caller hace `window.location.reload()` para que TanStack rehidrate
   * desde el nuevo char (MVP: hard-reload pattern, matches logout).
   */
  switchCharacter: (characterId: number) =>
    http<{ ok: boolean; active_character_id: number }>(
      `/auth/switch/${characterId}`,
      { method: 'POST' }
    ),

  walletBalance: (force = false) => http<{ balance: number }>(`/api/wallet/balance?force=${force}`),
  walletJournal: (page = 1, force = false) =>
    http<{ page: number; entries: Array<{
      id: number
      date: string
      ref_type: string
      amount: number
      balance: number
      description: string
    }> }>(`/api/wallet/journal?page=${page}&force=${force}`),

  /**
   * Wallet history — timeline del balance diaria para los últimos N días
   * (7 o 30). El backend pagina el journal hacia atrás hasta cubrir la
   * ventana y forward-filla días sin transacciones. Usado por el
   * sparkline del Dashboard. Subrequest budget: 1 balance + hasta 10 pages.
   */
  walletHistory: (days: 7 | 30 = 7, force = false) =>
    http<{
      days: number
      start_day: string
      end_day: string
      points: Array<{ date: string; balance: number }>
      start_balance: number
      end_balance: number
      change: number
      change_pct: number
      pages_walked: number
    }>(`/api/wallet/history?days=${days}&force=${force}`),

  miningLedger: (force = false) =>
    http<{ entries: Array<{
      date: string
      type_id: number
      name: string
      quantity: number
      unit_price: number | null
      total_value: number | null
    }> }>(
      `/api/mining/ledger?force=${force}`
    ),

  /**
   * Mining history Jita-valuated — el mining del char con precio al día vs
   * precio HOY de cada ore. Permite responder "¿lo que miné la semana
   * pasada subió o bajó de precio?" sin input del user.
   *
   * Caveat: ESI market history es SELL-order avg, no buy orders reales.
   * Directional trend es correcto; el ISK exacto sobreestima (los buy
   * orders serían ~3-10% menores).
   */
  miningValued: (force = false) =>
    http<{
      entries: Array<{
        date: string
        type_id: number
        name: string
        quantity: number
        unit_price_at_day: number | null
        unit_price_today: number | null
        value_at_day: number | null
        value_today: number | null
        delta_pct: number | null
      }>
      summary: {
        total_quantity: number
        total_value_at_day: number
        total_value_today: number
        delta_value: number
        delta_pct: number | null
        skipped_types: number
      }
    }>(`/api/mining/valued?force=${force}`),

  /**
   * Mining history — aggregado por día + por ore a Jita *buy*. Cero
   * input del user. Devuelve summary (total ISK, días activos, mejor
   * día, top ore) + by_day (timeline listo para graficar) + by_ore
   * (ranking con share_pct).
   *
   * Caveat: ESI mining ledger = últimos ~30 días. Para `days > 30`
   * devuelve vacío (no tenemos data aunque pidamos). El caller UI
   * avisa al user.
   */
  miningHistory: (
    params: { days?: number; currentMonth?: boolean },
    force = false,
  ) => {
    const qs = new URLSearchParams()
    if (params.currentMonth) qs.set('period', 'current_month')
    else qs.set('days', String(params.days ?? 30))
    qs.set('force', String(force))
    return http<{
      period: {
        kind: 'days' | 'current_month'
        days?: number
        start_date: string
        end_date: string
      }
      summary: {
        total_quantity: number
        total_value: number
        days_active: number
        total_days_in_period: number
        avg_value_per_day: number
        avg_value_per_active_day: number
        top_day: { date: string; value: number; quantity: number } | null
        top_ore: { type_id: number; name: string; value: number; quantity: number } | null
        skipped_types: number
        valued_share_pct: number
      }
      by_day: Array<{
        date: string
        quantity: number
        value: number
        ores: Array<{
          type_id: number
          name: string
          quantity: number
          line_value: number
          unit_price: number | null
        }>
      }>
      by_ore: Array<{
        type_id: number
        name: string
        quantity: number
        value: number
        unit_price: number | null
        share_pct: number
      }>
      skipped_types: number
    }>(`/api/mining/history?${qs.toString()}`)
  },

  /**
   * Personal mining stats — "Strava for industrialists". Agrupa el mining
   * ledger en sesiones (entries dentro de 30min = misma sesión), computa
   * ISK/min @ Jita buy, y devuelve:
   *  - top 5 sesiones (ranked por ISK/min)
   *  - sesión top + weekly summary (total/sesiones/mejor por semana)
   *  - streaks (racha actual + racha más larga del período)
   *  - resumen del período (total ISK, sesiones, avg, días activos)
   *
   * Mining ledger ESI = últimos ~30d (cap del propio ESI). Para
   * `days > 30` devolverá vacío porque no hay data; el caller lo avisa.
   */
  personalStats: (
    params: { days?: number; currentMonth?: boolean },
    force = false,
  ) => {
    const qs = new URLSearchParams()
    if (params.currentMonth) qs.set('period', 'current_month')
    else qs.set('days', String(params.days ?? 30))
    qs.set('force', String(force))
    return http<{
      period: {
        kind: 'days' | 'current_month'
        days?: number
        start_date: string
        end_date: string
      }
      summary: {
        total_value: number
        total_qty: number
        total_sessions: number
        avg_session_min: number
        avg_value_per_active_day: number
        days_active: number
        total_days_in_period: number
        top_session: {
          start: string
          end: string
          duration_min: number
          total_value: number
          isk_per_min: number
          isk_per_hour: number
        } | null
        current_streak: number
        longest_streak: number
        longest_streak_dates: { from: string; to: string } | null
      }
      top_sessions: Array<{
        session_id: string
        start: string
        end: string
        duration_min: number
        total_qty: number
        total_value: number
        isk_per_min: number
        isk_per_hour: number
        day: string
        ores: Record<
          string,
          { type_id: number; name: string; quantity: number; line_value: number }
        >
      }>
      weekly_summary: Array<{
        week_start: string
        week_end: string
        total_value: number
        total_qty: number
        total_sessions: number
        best_session: { start: string; isk_per_min: number } | null
      }>
      streak: {
        current_streak_days: number
        longest_streak_days: number
        last_mined_day: string | null
      }
    }>(`/api/mining/stats?${qs.toString()}`)
  },

  /**
   * "Jita avg 30d" — para listas de type_ids, devuelve el avg de las
   * sell orders de los últimos 30 días (ESI history). Frontend computa
   * delta = (current - avg) / avg. Usado por Refinery y Utility para
   * mostrar pill "−8% vs avg 30d" / "+12% vs avg 30d" en cada precio.
   *
   * Caveat: ESI history es sell-order avg (~3-10% sobre buy orders).
   * Trend direction OK, ISK exacto puede mentir.
   *
   * Cap 30 type_ids en el backend (subrequest budget).
   */
  marketsContext: (typeIds: number[], force = false) =>
    http<{
      contexts: Array<{
        type_id: number
        avg_30d: number | null
        window_start: string | null
        window_end: string | null
      }>
      skipped: number
    }>(
      `/api/markets/context?type_ids=${typeIds.join(',')}&force=${force}`,
    ),

  skillQueue: (force = false) =>
    http<{ queue: Array<{
      skill_id: number
      name: string
      finished_level: number
      start_date: string | null
      finish_date: string | null
    }> }>(`/api/skills/queue?force=${force}`),

  skills: (force = false) =>
    http<{
      skills: Array<{
        skill_id: number
        name: string
        active_level: number
        trained_level: number
        sp: number
      }>
      total_sp: number
      unallocated_sp: number
    }>(`/api/skills/list?force=${force}`),

  /**
   * Skill impact analyzer — "¿qué skills me rinden más entrenarlas?".
   * Toma los skills del char + mining ledger + Jita prices. Por skill
   * crítica (Reprocessing/Reproc Eff/Astrogeology/Mining Barges) computa
   * Δ ISK/mes del próximo nivel + cumulativo a MAX.
   */
  skillImpact: (
    params: { days?: number; currentMonth?: boolean },
    force = false,
  ) => {
    const qs = new URLSearchParams()
    if (params.currentMonth) qs.set('period', 'current_month')
    else qs.set('days', String(params.days ?? 30))
    qs.set('force', String(force))
    return http<{
      period: {
        kind: 'days' | 'current_month'
        days?: number
        start_date: string
        end_date: string
      }
      observed_days: number
      reprocess_value_jita_buy_30d: number
      mining_value_jita_buy_30d: number
      skills: Array<{
        skill_id: number
        name: string
        kind: 'reprocessing' | 'mining_yield'
        current_level: number
        max_level: number
        next_level: number | null
        delta_per_level_pct: number
        next_level_delta_isk_per_month: number | null
        cumulative_delta_isk_to_max: number | null
        remaining_levels: number
        trained: boolean
        reasoning: string
      }>
    }>(`/api/skills/impact?${qs.toString()}`)
  },

  industryJobs: (includeCompleted = false, force = false) =>
    http<{ jobs: Array<{
      job_id: number
      activity_id: number
      activity_name: string
      blueprint_type_id: number
      blueprint_name: string
      product_type_id: number | null
      runs: number
      status: 'active' | 'paused' | 'ready' | 'delivered' | 'cancelled' | 'reverted'
      start_date: string
      end_date: string
      progress: number
      remaining_seconds: number
    }> }>(`/api/industry/jobs?include_completed=${includeCompleted}&force=${force}`),

  /**
   * "¿Qué ore minar ahora mismo?" Ranking de ores por ISK/h usando precios
   * Jita buy actuales. El user controla la tasa de minería (m³/s) desde
   * el slider; el default 8 ≈ Procurer/Retriever con Modulated Strip Miners II.
   *
   * Endpoint público (sin requireAuth): es data de mercado estática + tabla
   * de yields, no toca ESI per-char. Cero data personal expuesta.
   */
  oreProfitability: (miningRateM3PerSec = 8) =>
    http<{
      mining_rate_m3_per_sec: number
      jita_prices_ts: number | null
      ores: Array<{
        type_id: number
        name: string
        tier: 'high-sec' | 'low-sec' | 'null-sec'
        yields: Array<{
          mineral_type_id: number
          mineral_name: string
          yield_per_100: number
          jita_buy: number | null
          isk_per_hour: number
          pct_of_total: number
        }>
        total_isk_per_hour: number
      }>
    }>(`/api/mining/ore-profitability?mining_rate_m3_per_sec=${miningRateM3PerSec}`),

  /**
   * Mismo /api/industry/jobs pero con Jita sell price por producto +
   * valor esperado del job + margin real (manufacturing/reactions only).
   * Para la página /industry — muestra "este job vale 45M ISK a Jita
   * ahora" + ISK/hora, y para manufacturing/reactions también materials
   * cost, margin total, margin/h (el número clave del industrialist) y
   * margin %. Research jobs no llevan margin (note = "Research job — no
   * material cost").
   */
  industryValuation: (
    includeCompleted = false,
    force = false,
    materialsSource: 'fuzzwork' | 'esi' = 'fuzzwork',
  ) =>
    http<{
      jobs: Array<{
        job_id: number
        activity_id: number
        activity_name: string
        blueprint_type_id: number
        blueprint_name: string
        product_type_id: number | null
        product_name: string | null
        runs: number
        status: 'active' | 'paused' | 'ready' | 'delivered' | 'cancelled' | 'reverted'
        start_date: string
        end_date: string
        progress: number
        remaining_seconds: number
        product_jita_sell: number | null
        expected_total_value: number | null
        expected_value_per_hour: number | null
        materials: Array<{
          type_id: number
          name: string
          quantity: number
          jita_buy: number | null
          line_cost: number
        }> | null
        material_cost_per_run: number | null
        material_cost_total: number | null
        facility_cost_total: number | null
        margin_per_run: number | null
        margin_total: number | null
        margin_per_hour: number | null
        margin_pct: number | null
        materials_note: string | null
        // 'esi' cuando el backend usó ESI per-job materials (skills +
        // structure + rig bonuses aplicados). 'fuzzwork' cuando usó el
        // SDE sin bonos. Null para research jobs.
        materials_source: 'esi' | 'fuzzwork' | null
      }>
      summary: {
        total_active_value: number
        total_active_count: number
        valued_count: number
        unpriced_count: number
        total_margin: number
        marginable_jobs: number
        profitable_jobs: number
        avg_margin_pct: number
        manufacturing_margin: number
        reactions_margin: number
      }
    }>(
      `/api/industry/valuation?include_completed=${includeCompleted}&force=${force}&materials_source=${materialsSource}`,
    ),

  /**
   * Margin history — daily snapshots del summary de /api/industry/valuation
   * Persistido en D1 (migrations/0003_margin_history.sql) por un cron diario
   * (1 user/día, round-robin) + un POST /snapshot on-demand.
   *
   * El backend hace forward-fill de días sin data, así que `snapshots` siempre
   * tiene `days` puntos consecutivos. `is_forward_filled: true` indica
   * puntos sintéticos (sin snapshot real ese día).
   */
  industryMarginHistory: (days = 30) =>
    http<{
      days: number
      snapshot_count: number
      snapshots: Array<{
        date: string  // YYYY-MM-DD UTC
        total_margin: number
        marginable_jobs: number
        profitable_jobs: number
        avg_margin_pct: number
        total_active_count: number
        valued_count: number
        is_forward_filled: boolean
      }>
    }>(`/api/industry/margin-history?days=${days}`),

  /**
   * Manual margin snapshot — captura el estado actual del user en D1.
   * Idempotente por día (PK sobre character_id, snapshot_date). Útil para
   * llenar gaps del cron diario y dar feedback inmediato post-deploy.
   */
  industryMarginSnapshot: () =>
    http<{
      ok: boolean
      snapshot_date: string
      total_margin: number
    }>('/api/industry/margin-history/snapshot', { method: 'POST' }),

  /**
   * "Qué asteroides minar para fabricar X" — recibe un type_id (BP o
   * product), devuelve los materiales + top 3 ores por material rankeadas
   * por least ore to mine.
   *
   * Variantes:
   *   - blueprintMinerals(typeId, ...) — por type_id numérico
   *   - blueprintMineralsByName(name, ...) — por nombre exacto (Fuzzwork
   *     typeid.php — case-sensitive, no fuzzy). 404 si no hay match.
   */
  blueprintMinerals: (
    typeId: number,
    runs = 1,
    refiningEfficiency = 0.7,
  ) =>
    http<BlueprintMineralsResponse>(
      `/api/blueprint-minerals?type_id=${typeId}&runs=${runs}&refining_efficiency=${refiningEfficiency}`,
    ),

  blueprintMineralsByName: (
    name: string,
    runs = 1,
    refiningEfficiency = 0.7,
  ) =>
    http<BlueprintMineralsResponse>(
      `/api/blueprint-minerals?name=${encodeURIComponent(name)}&runs=${runs}&refining_efficiency=${refiningEfficiency}`,
    ),

  /**
   * Auto-suggest next BP — "¿qué fabrico?" Ranking de los productos que el
   * char fabricó en los últimos 30 días, por margin/h × materials_coverage.
   *
   * Solo considera productos del historial reciente — no enumera BPs nuevos
   * que podrías intentar. Para v1 eso es OK (sirve para "manufacturar de
   * nuevo lo que sé que rinde"). Si `suggestions: []`, hay `reason` y
   * `message` explicando por qué.
   */
  industrySuggestions: (limit = 5) =>
    http<{
      suggestions: Array<{
        product_type_id: number
        product_name: string | null
        blueprint_name: string | null
        last_manufactured: string | null
        manufacture_count_30d: number
        current_margin_per_run: number | null
        current_margin_per_hour: number | null
        current_margin_pct: number | null
        materials_coverage_pct: number
        materials_needed: number
        materials_in_hangar: number
        score: number | null
        recommendation:
          | 'ready_to_manufacture'
          | 'mostly_ready'
          | 'needs_materials'
          | 'no_margin_data'
      }>
      total_candidates: number
      reason?: string
      message?: string
    }>(`/api/industry/suggestions?limit=${limit}`),

  /**
   * Industry notifications — "¿qué jobs nuevos arrancaron?".
   * On-demand detect: el backend compara ESI jobs contra D1
   * industry_job_seen, inserta los nuevos, devuelve los unseen
   * con margin/h estimado.
   */
  industryNotifications: () =>
    http<{
      notifications: Array<{
        job_id: number
        activity_id: number
        activity_name: string
        blueprint_type_id: number
        blueprint_name: string | null
        product_type_id: number | null
        runs: number
        status: 'active' | 'paused' | 'ready' | 'delivered' | 'cancelled' | 'reverted'
        start_date: string
        estimated_margin_per_hour: number | null
        materials_note: string | null
      }>
      unseen_count: number
    }>('/api/industry/notifications'),

  /** Bulk-ack: marca todos los unseen del user como notified=1. */
  industryNotificationsAck: () =>
    http<{ acked_count: number }>(
      '/api/industry/notifications/ack',
      { method: 'POST' },
    ),

  /**
   * Daily ISK flow — "¿cuánto gané hoy?". Agrega mining ledger + journal
   * positivo del día UTC. 0 subrequests warm (KV), 2 cold. El dashboard
   * lo usa para mostrar el tile "Today" sin que el user haga nada.
   */
  dashboardToday: (force = false) =>
    http<{
      date: string
      range: { start: string; end: string }
      mining: {
        isk_today: number
        units_today: number
        ores_mined: number
        top_ore: {
          type_id: number
          name: string
          value: number
          quantity: number
        } | null
      }
      income: {
        isk_today: number
        transactions_today: number
        top_ref_type: { ref_type: string; total: number; count: number } | null
      }
      net_today: number
      note?: string
    }>(`/api/dashboard/today?force=${force}`),

  /**
   * Cross-char aggregate — "tu imperio". Suma wallet + SP + active jobs a
   * través de todos los chars del dashboard_user (cap 6, KV cache 5min).
   *
   * Endpoint sin inventory_value ni margin de jobs (subrequest budget).
   * Para esos, drill-down a la página por char.
   *
   * Failure semantics:
   *   - Si TODOS los chars fallan → 503 (http() lo tira como ApiError).
   *   - Si al menos 1 funciona → 200 con `chars[].error` por char que falló.
   */
  dashboardAggregate: (force = false) =>
    http<{
      computed_at: string
      totals: {
        wallet: number
        total_sp: number
        active_jobs_count: number
        pi_colonies_count: number
        pi_total_pins: number
        chars_count: number
      }
      chars: Array<{
        id: number
        name: string
        portrait: string | null
        corporation_id: number
        is_active: boolean
        wallet: number | null
        total_sp: number | null
        active_jobs_count: number
        pi_colonies_count: number
        pi_total_pins: number
        last_login_at: string
        error?: string
      }>
      cached: boolean
      note?: string
      error?: string
    }>(`/api/dashboard/aggregate?force=${force}`),

  /**
   * "Cómo viene mi mes (en ISK)". N-day ISK flow del active char:
   *   - summary.total_net  = income - expense + mining
   *   - summary.best_day    = día con mayor net
   *   - by_day[]            = serie diaria para mini-bars (income / mining / net
   *                            per día, ready para graficar inline)
   *
   * Default 30 días = "este mes" mental model. Cap de 60d.
   */
  dashboardMonth: (days = 30, force = false) =>
    http<{
      days: number
      start_day: string
      end_day: string
      summary: {
        total_income: number
        total_mining: number
        total_net: number
        days_active: number
        best_day: { date: string; net: number } | null
        journal_pages_walked: number
      }
      by_day: Array<{
        date: string
        income: number
        expense: number
        mining: number
        net: number
        top_income_ref_type: { ref_type: string; total: number } | null
      }>
      cached: boolean
      note?: string
    }>(`/api/dashboard/month?days=${days}&force=${force}`),

  /** Ack individual — saca un solo job del bell. */
  industryNotificationsAckOne: (jobId: number) =>
    http<{ ok: boolean }>(
      `/api/industry/notifications/${jobId}`,
      { method: 'DELETE' },
    ),

  /**
   * Planetary Industry — colonies del user.
   * Lista resumida (system, planet type, pins, age) y detalle (pins con
   * extractors, factories, storage). KV-cached 30min en backend.
   */
  planetaryColonies: (force = false) =>
    http<{
      colonies: Array<{
        planet_id: number
        solar_system_id: number
        solar_system_name: string | null
        planet_type: string
        planet_type_name: string
        upgrade_level: number
        num_pins: number | null
        last_update: string | null
        age_hours: number | null
      }>
      count: number
    }>(`/api/planetary/colonies?force=${force}`),

  planetaryColony: (planetId: number, force = false) =>
    http<{
      planet_id: number
      solar_system_id: number
      solar_system_name: string | null
      planet_type: string
      planet_type_name: string
      upgrade_level: number
      num_pins: number | null
      last_update: string | null
      age_hours: number | null
      pins: Array<{
        pin_id: number
        type_id: number
        type_name: string | null
        latitude: number
        longitude: number
        role: 'extractor' | 'factory' | 'storage' | 'command' | 'launchpad' | 'unknown'
        extractors?: Array<{
          product_type_id: number
          product_name: string | null
          quantity_per_cycle: number
          cycle_time_hours: number
          expiry_time: string
          expires_in_hours: number
          expiring_soon: boolean
          /** Jita buy price del product (5min KV cache). Null si sin orders. */
          jita_buy: number | null
          /** ISK que produce cada cycle completo. Null si no hay jita_buy. */
          isk_per_cycle: number | null
          /** ISK/h promedio = isk_per_cycle / cycle_time_hours. */
          isk_per_hour: number | null
        }>
        contents?: Array<{ type_id: number; name: string | null; quantity: number }>
        schematic_id?: number
      }>
      counts: {
        total: number
        extractors: number
        factories: number
        storage: number
        expiring_soon: number
      }
    }>(`/api/planetary/colonies/${planetId}?force=${force}`),

  /**
   * Top N extractors que vencen primero across todas las colonies del char.
   * Para el "Next actions" tile del Dashboard — el industrialist ve
   * "tu próximo extractor vence en 4h 12m, Helium Isotopes, +12.4M ISK/h"
   * sin abrir /planetary.
   *
   * Backend hace fan-out: list + N details + Jita prices. Cold ~30-50
   * subrequests (depende de # colonies y # extractors). Warm (KV-cached
   * 30min en colony detail, 5min en prices): 0. No cacheamos la
   * respuesta aggregate — cambia muy seguido.
   */
  planetaryExpiring: (hours = 168, limit = 5, force = false) =>
    http<{
      extractors: Array<{
        planet_id: number
        planet_type: string
        planet_type_name: string
        solar_system_id: number
        solar_system_name: string | null
        product_type_id: number
        product_name: string | null
        quantity_per_cycle: number
        cycle_time_hours: number
        expiry_time: string
        expires_in_hours: number
        jita_buy: number | null
        isk_per_cycle: number | null
        isk_per_hour: number | null
      }>
      count: number
      failed_planets: number[]
      note?: string
    }>(`/api/planetary/expiring?hours=${hours}&limit=${limit}&force=${force}`),

  corpWallets: () =>
    http<{ balances: Array<{ division: number; balance: number }> }>('/api/corp/wallets'),

  corpProfile: () =>
    http<{
      profile: {
        corporation_id: number
        name: string
        ticker: string
        member_count: number
        tax_rate: number
        alliance_id: number | null
        alliance_name?: string
        alliance_ticker?: string
        ceo_id: number
        date_founded: string | null
      }
    }>('/api/corp/profile'),

  taxPreview: (
    params: { days?: number; currentMonth?: boolean },
    force = false,
  ) => {
    const qs = new URLSearchParams()
    if (params.currentMonth) qs.set('period', 'current_month')
    else qs.set('days', String(params.days ?? 30))
    qs.set('force', String(force))
    return http<{
      period: {
        kind: 'days' | 'current_month'
        days?: number
        start_date: string
        end_date: string
      }
      mining: {
        gross_value: number
        entries: Array<{
          date: string
          type_id: number
          name: string
          quantity: number
          unit_price: number | null
          total_value: number | null
        }>
      }
      refining: {
        gross_value: number
        jobs: Array<{
          job_id: number
          end_date: string
          blueprint_type_id: number
          blueprint_name: string
          product_type_id: number
          product_name: string
          runs: number
          unit_price: number | null
          total_value: number | null
        }>
      }
      reactions: {
        gross_value: number
        jobs: Array<{
          job_id: number
          end_date: string
          blueprint_type_id: number
          blueprint_name: string
          product_type_id: number
          product_name: string
          runs: number
          unit_price: number | null
          total_value: number | null
        }>
      }
      total_gross: number
    }>(`/api/tax/preview?${qs.toString()}`)
  },

  utilitySellAdvice: (force = false, offset = 0, limit = 20) =>
    http<{
      location: {
        station_id: number | null
        solar_system_id: number | null
        kind: 'station' | 'structure' | 'in_space' | 'unknown'
        region_id: number | null
      }
      items: Array<{
        type_id: number
        name: string
        quantity: number
        is_ore: boolean
        jita: { sell_price: number | null }
        best_action: {
          verdict: 'haul_jita' | 'insufficient_data'
          reason: string
          delta_pct: number | null
        }
      }>
      page: {
        offset: number
        limit: number
        total: number
        has_more: boolean
        current_page: number
        total_pages: number
      }
      summary: {
        total_items: number
        haul_wins: number
        priced: number
        total_value_if_followed: number
      }
      _debug?: {
        total_unique_types: number
        priced_this_page: number
        cap: number
      }
    }>(`/api/utility/sell-advice?force=${force}&offset=${offset}&limit=${limit}`),

  /**
   * "Vendé acá" — para cada item del hangar dockeado, mira el order book
   * local (NPC station / player structure) y recomienda un precio de lista
   * vs Jita. Diferencia con sell-advice:
   *
   *   - sell-advice = macro: "¿lo llevo a Jita?"
   *   - sell-here   = táctico: "estoy acá, ¿a cuánto lo listo?"
   *
   * Cap MAX_LOCAL_TYPES=20 (top por qty) en el backend. KV-cachea market
   * orders por (scope, type_id) 5 min.
   *
   * Subrequest budget total: ~25 (location + structure resolve + assets +
   * names + char orders + hasta 20 market orders). Well under 50.
   */
  utilitySellHere: (force = false) =>
    http<{
      location: {
        kind: 'station' | 'structure' | 'in_space' | 'unknown'
        station_id: number | null
        structure_id: number | null
        solar_system_id: number | null
        region_id: number | null
      }
      items: Array<{
        type_id: number
        name: string
        quantity: number
        best_local_sell: { price: number; volume: number } | null
        best_local_buy: { price: number; volume: number } | null
        local_sell_count: number
        local_buy_count: number
        your_existing_orders: number
        jita_sell: number | null
        recommended_price: number | null
        advice: {
          verdict:
            | 'sell_here_undercut'
            | 'sell_here_match'
            | 'sell_here_premium'
            | 'haul_jita'
            | 'no_local_market'
            | 'insufficient_data'
          reason: string
          delta_vs_jita_pct: number | null
        }
      }>
      summary: {
        total_items_analyzed: number
        total_items_skipped: number
        sell_here_wins: number
        haul_wins: number
        no_local_market: number
        insufficient_data: number
        total_value_if_followed: number
      }
    }>(`/api/utility/sell-here?force=${force}`),

  /**
   * Compression advisor — "¿comprimo este ore antes de haul-ear?".
   *
   * Para cada ore en el hangar dockeado:
   *   - Volume actual (m³) y volume si lo comprimes
   *   - m³ saved
   *   - Compression cost (configurable) vs hauling value saved (configurable)
   *   - Verdict: compress / sell_uncompressed / indifferent / insufficient_data
   *
   * Settings (query params, default 0 cada uno):
   *   - compression_tax: ISK por unidad uncompressed (player structure = 0,
   *     NPC ≈ 5-10). Overrideable via localStorage en el frontend.
   *   - hauling_cost: ISK/m³ (own freighter = 0, public courier = ~500-2000).
   *
   * Arkonor y Mercoxit no tienen variant comprimida → insufficient_data.
   */
  utilityCompressionAdvice: (
    params: { compression_tax?: number; hauling_cost?: number },
    force = false,
  ) => {
    const qs = new URLSearchParams()
    qs.set('compression_tax', String(params.compression_tax ?? 0))
    qs.set('hauling_cost', String(params.hauling_cost ?? 0))
    qs.set('force', String(force))
    return http<{
      location: {
        kind: 'in_space' | 'unknown'
        station_id: number | null
        solar_system_id: number | null
        region_id: number | null
      }
      settings_used: {
        compression_tax_per_unit: number
        hauling_cost_per_m3: number
        indifferent_threshold_isk: number
      }
      items: Array<{
        type_id: number
        name: string
        form: 'uncompressed' | 'compressed' | 'unknown'
        quantity: number
        compressed_type_id: number | null
        compressed_qty_equivalent: number | null
        volume_per_unit: number | null
        volume_total_m3: number | null
        compressed_volume_per_unit: number | null
        compressed_volume_total_m3: number | null
        m3_saved: number | null
        jita_sell_price: number | null
        jita_compressed_sell_price: number | null
        sell_value_total: number | null
        compressed_value_total: number | null
        compression_cost: number | null
        hauling_value_saved: number | null
        net_benefit: number | null
        verdict: 'compress' | 'sell_uncompressed' | 'indifferent' | 'insufficient_data'
        reason: string
      }>
      summary: {
        total_ores: number
        compress_wins: number
        sell_wins: number
        indifferent: number
        insufficient_data: number
        total_m3_saved: number
        total_compression_cost: number
        total_hauling_value_saved: number
        total_net_benefit: number
      }
    }>(`/api/utility/compression-advice?${qs.toString()}`)
  },

  /**
   * Pipeline ISK/hour per ore — "¿cuánto gano por hora si mino X ore?".
   *
   * Para cada uno de los 16 ores estándar computa 3 pipelines (sell_ore /
   * refine / compress) y devuelve el ranking por best ISK/hour.
   *
   * Settings (query params, defaults conservadores):
   *   - mining_rate_m3_per_min: cuánta m³/min saca tu stripper (300 = Procurer base)
   *   - final_yield_pct: yield efectivo de tu reprocess (50 = NPC base)
   *   - refining_tax_pct: 5 = NPC station, 0 = refinery propio
   */
  pipelineAdvice: (
    settings: { mining_rate_m3_per_min?: number; final_yield_pct?: number; refining_tax_pct?: number },
    force = false,
  ) => {
    const qs = new URLSearchParams()
    qs.set('mining_rate_m3_per_min', String(settings.mining_rate_m3_per_min ?? 300))
    qs.set('final_yield_pct', String(settings.final_yield_pct ?? 50))
    qs.set('refining_tax_pct', String(settings.refining_tax_pct ?? 5))
    qs.set('force', String(force))
    return http<{
      settings_used: {
        mining_rate_m3_per_min: number
        m3_per_hour: number
        final_yield_pct: number
        refining_tax_pct: number
      }
      summary: {
        total_ores: number
        ores_with_data: number
        top_ore_id: number | null
        top_ore_name: string | null
        top_isk_per_hour: number | null
        avg_isk_per_hour: number | null
        sell_wins: number
        refine_wins: number
        compress_wins: number
        insufficient_data: number
      }
      items: Array<{
        type_id: number
        name: string
        volume_per_unit: number | null
        group_id: number
        pipelines: {
          sell_ore: { value_per_m3: number | null; value_per_unit: number | null; isk_per_hour: number | null }
          refine:    { value_per_m3: number | null; value_per_unit: number | null; isk_per_hour: number | null }
          compress:  { value_per_m3: number | null; value_per_unit: number | null; isk_per_hour: number | null }
        }
        best_action: 'sell_ore' | 'refine' | 'compress' | 'insufficient_data'
        best_isk_per_hour: number | null
      }>
    }>(`/api/utility/pipeline-advice?${qs.toString()}`)
  },

  /**
   * Sell orders tracker — tus sell orders activas en todos los hubs.
   * Devuelve metadata enriquecida: type name, location name, qty remain,
   * total ISK locked, días hasta expiración, status (active / expiring_soon).
   * Agrupado por ubicación para vista tipo "portfolio".
   */
  activeSellOrders: (force = false) =>
    http<{
      summary: {
        total_orders: number
        total_locations: number
        total_isk_locked: number
        total_units_remaining: number
        expiring_soon: number
      }
      groups: Array<{
        location_id: number
        location_name: string
        orders: Array<{
          order_id: number
          type_id: number
          type_name: string
          location_id: number
          location_name: string
          price: number
          volume_total: number
          volume_remain: number
          total_isk: number
          issued: string
          expires: string
          duration_days: number
          days_until_expiry: number
          status: 'active' | 'expiring_soon' | 'expired'
          range: string
          min_volume: number
        }>
        total_isk: number
        total_volume: number
      }>
      items: Array<{
        order_id: number
        type_id: number
        type_name: string
        location_id: number
        location_name: string
        price: number
        volume_total: number
        volume_remain: number
        total_isk: number
        issued: string
        expires: string
        duration_days: number
        days_until_expiry: number
        status: 'active' | 'expiring_soon' | 'expired'
        range: string
        min_volume: number
      }>
    }>(`/api/utility/active-sell-orders?force=${force}`),

  /**
   * Hub comparison — "¿dónde vendo mejor este item?"
   * Top-N items del hangar dockeado, comparados por mejor buy order en
   * 5 hubs (Jita, Amarr, Dodixie, Rens, Hek).
   */
  hubComparison: (force = false) =>
    http<{
      location: {
        station_id: number | null
        solar_system_id: number | null
        kind: 'in_space' | 'unknown'
      }
      settings_used: {
        top_n: number
        hubs: Array<{ short: string; name: string; region_id: number }>
      }
      summary: {
        items_analyzed: number
        total_items_in_hangar: number
        jita_wins: number
        other_hub_wins: number
        no_data: number
        best_opportunity: {
          type_id: number
          name: string
          best_hub: string | null
          delta_vs_jita_pct: number | null
          best_total: number | null
        } | null
      }
      items: Array<{
        type_id: number
        name: string
        quantity: number
        hub_prices: Record<
          string,
          { price: number | null; region_id: number }
        >
        best_hub: string | null
        best_price: number | null
        best_total: number | null
        delta_vs_jita_pct: number | null
      }>
    }>(`/api/utility/hub-comparison?force=${force}`),

  /**
   * Pricing board — "¿a qué precio EXACTO pongo esto?". Top 5 sell orders
   * locales (ladder) + 3 sugerencias (undercut_1/match/undercut_5pct) con
   * queue depth + revenue estimada si vendés toda la qty a esa opción.
   * Complementa /sell-here (macro verdict) con el detalle concreto.
   */

  /**
   * Inter-hub arbitrage — "¿me conviene haul-ear a otro hub?".
   * Para los top-N items del hangar dockeado, mira el BEST BUY order en
   * los 5 hubs + local, computa el net Δ después de un hauling cost
   * configurable (default 500 ISK/m³), y devuelve el verdict
   * ("haul_to_hub" si conviene, "sell_local" si no, "indifferent",
   * "insufficient_data").
   */
  interhubArbitrage: (haulIskPerM3 = 500, force = false) =>
    http<{
      location: {
        kind: 'station' | 'structure' | 'in_space'
        station_id: number | null
        solar_system_id: number | null
        region_id: number | null
      }
      settings_used: {
        haul_isk_per_m3: number
        top_n: number
        hubs: Array<{ short: string; name: string; region_id: number }>
      }
      summary: {
        total_haul_to_hub: number
        total_sell_local: number
        total_indifferent: number
        total_insufficient_data: number
        net_gain_total: number
      }
      items: Array<{
        type_id: number
        name: string
        quantity: number
        volume_per_unit: number | null
        is_ore: boolean
        local: {
          best_buy: { price: number; volume_remain: number } | null
          buy_count: number
          region_id: number | null
        }
        hubs: Array<{
          hub_short: string
          region_id: number
          best_buy: { price: number; volume_remain: number } | null
          buy_order_count: number
        }>
        best_hub: { short: string; price: number; volume_remain: number } | null
        best_hub_vs_local: { delta_isk_per_unit: number; delta_pct: number } | null
        hauling_cost_per_unit: number | null
        net_delta_isk_per_unit: number | null
        net_breakeven_haul_isk_per_m3: number | null
        verdict: 'haul_to_hub' | 'sell_local' | 'indifferent' | 'insufficient_data'
        reason: string
      }>
    }>(`/api/utility/interhub-arbitrage?haul_isk_per_m3=${haulIskPerM3}&force=${force}`),

  pricingBoard: (force = false) =>
    http<{
      location: {
        kind: 'station' | 'structure' | 'in_space'
        station_id: number | null
        structure_id: number | null
        solar_system_id: number | null
        region_id: number | null
      }
      summary: {
        total_items_analyzed: number
        local_market_available: boolean
        items_with_ladder: number
      }
      items: Array<{
        type_id: number
        name: string
        quantity: number
        ladder: Array<{ price: number; volume_remain: number }>
        best_local_sell: { price: number; volume_remain: number } | null
        jita_sell: number | null
        suggestions: {
          undercut_1: { price: number; queue_depth: number }
          match: { price: number; queue_depth: number }
          undercut_5pct: { price: number; queue_depth: number } | null
        }
        your_existing_orders: number
        revenue_at: {
          undercut_1: number | null
          match: number | null
          undercut_5pct: number | null
        }
        note?: string
      }>
      skipped: number
    }>(`/api/utility/pricing-board?force=${force}`),

  /**
   * Wallet breakdown — "¿de dónde viene tu ISK? ¿a dónde se va?"
   * Agrupa el journal por categoría + ref_type + día. Devuelve summary,
   * by_category, by_ref_type, by_day, top 5 income/expense.
   */
  walletBreakdown: (days = 30, force = false) =>
    http<{
      days: number
      start_day: string
      end_day: string
      summary: {
        total_entries: number
        total_income: number
        total_expense: number
        net: number
      }
      by_category: Array<{
        category: string
        count: number
        income: number
        expense: number
        net: number
      }>
      by_ref_type: Array<{
        ref_type: string
        category: string
        count: number
        income: number
        expense: number
        net: number
      }>
      by_day: Array<{
        date: string
        income: number
        expense: number
        net: number
      }>
      top_income: Array<{
        id: number
        date: string
        ref_type: string
        category: string
        amount: number
        description: string
      }>
      top_expense: Array<{
        id: number
        date: string
        ref_type: string
        category: string
        amount: number
        description: string
      }>
    }>(`/api/wallet/breakdown?days=${days}&force=${force}`),

  /**
   * Asset overview — todos los hangares del char con su valor Jita estimado.
   * Devuelve grupos por location_id con sus items (top 20 por valor) + total.
   */
  inventoryAllStations: (force = false) =>
    http<{
      summary: {
        total_locations: number
        total_asset_rows: number
        priced_count: number
        unpriced_count: number
        total_value: number
      }
      groups: Array<{
        location_id: number
        location_name: string
        location_type: string
        total_value: number
        total_item_types: number
        item_count: number
        items: Array<{
          type_id: number
          name: string
          quantity: number
          unit_price: number | null
          total_value: number | null
        }>
      }>
    }>(`/api/inventory/all-stations?force=${force}`),

  reprocessAdvice: (settings: {
    skill_reprocess: number
    skill_reprocess_efficiency: number
    implant_pct: 0 | 4 | 6 | 10
    station_bonus_pct: 0 | 1 | 3 | 6
    refining_tax_pct: 0 | 2 | 5
  }, force = false) =>
    http<{
      location: {
        station_id: number | null
        solar_system_id: number
        kind: 'station' | 'structure' | 'in_space' | 'unknown'
      }
      settings_used: {
        skill_reprocess: number
        skill_reprocess_efficiency: number
        implant_pct: number
        station_bonus_pct: number
        refining_tax_pct: number
        final_yield_pct: number
      }
      summary: {
        total_ore_count: number
        sell_total: number
        reprocess_total: number
        delta_total: number
        reprocess_wins: number
        marginal: number
        sell_wins: number
      }
      items: Array<{
        type_id: number
        name: string
        quantity: number
        sell_value_per_unit: number | null
        reprocess_value_per_unit: number | null
        best_action: {
          verdict: 'reprocess' | 'sell_ore' | 'marginal' | 'insufficient_data'
          reason: string
          delta_pct: number | null
        }
        mineral_breakdown: Array<{
          mineral_id: number
          mineral_name: string
          units_per_unit: number
          unit_price: number | null
        }>
      }>
    }>(`/api/utility/reprocess-advice?force=${force}`, {
      method: 'POST',
      body: JSON.stringify(settings),
    }),

  refineryContext: (force = false) =>
    http<{
      skill_reprocess: 0 | 1 | 2 | 3 | 4 | 5
      skill_reprocess_efficiency: 0 | 1 | 2 | 3 | 4 | 5
      // null = no se pudo detectar (no hay reprocessing implant instalado,
      // o el match por nombre no encontró nada en dogma_attributes).
      reprocessing_implant_pct: 0 | 4 | 6 | 10 | null
      reprocessing_implant_name: string | null
      at_player_structure: boolean
      suggested_station_bonus_pct: 0 | 1 | 3 | 6
      suggested_refining_tax_pct: 0 | 2 | 5
      // null = no se pudo detectar (sin standings accesibles, o standing
      // <5.0). El frontend lo muestra como hint para que el user sepa
      // de dónde viene el valor y pueda auditarlo.
      station_bonus_source: string | null
      refining_tax_source: string | null
    }>(`/api/character/refinery-context?force=${force}`),

  /**
   * "¿Cuánto vale mi hangar hoy?" — total de assets al hangar dockeado
   * valorado a Jita sell. Usado por el dashboard tile. Sin paginación
   * (cap interno a 47 type_ids en el backend para cuidar el subrequest
   * budget). Para ver el breakdown / haul-to-Jita verdicts, ir a /utility.
   */
  inventoryValue: (force = false) =>
    http<{
      location: {
        station_id: number | null
        solar_system_id: number
      }
      total_value: number
      item_count: number
      priced_count: number
      unpriced_count: number
      // Items únicos que no pudimos prorratear (cap del backend o no
      // marketable). Sirve como sanity check — si es alto, el total está
      // subestimado.
      skipped_pricing: number
      top_items: Array<{
        type_id: number
        name: string
        quantity: number
        unit_price: number | null
        total_value: number | null
      }>
    }>(`/api/inventory/value?force=${force}`),

  /**
   * Próximos N eventos del calendario del active char (sorted event_date
   * ASC). Default 3, cap 10. Devuelve empty + `note: 'scope_missing'` si
   * el token del char no tiene `esi-calendar.read_calendar_events.v1`
   * (re-auth pendiente). El widget muestra un banner de re-auth en ese
   * caso.
   */
  calendarUpcoming: (limit = 3, force = false) =>
    http<{
      events: Array<{
        event_id: number
        event_date: string
        title: string
        duration: number
        response: 'accepted' | 'declined' | 'tentative' | 'undecided'
        importance: boolean
        owner_id: number
        owner_name: string
        owner_type: 'character' | 'corporation' | 'alliance' | 'faction'
      }>
      count: number
      note?: string
    }>(`/api/calendar/upcoming?limit=${limit}&force=${force}`),
}

export function formatISK(n: number | null | undefined, fraction = 2): string {
  // Toleramos undefined/null/NaN para sobrevivir deploys desincronizados
  // (web deploya antes que api, o API vieja devuelve shape anterior).
  if (n == null || !Number.isFinite(n)) return '—'
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: fraction,
    maximumFractionDigits: fraction,
  }).format(n)
}

export function formatISKShort(n: number | null | undefined): string {
  // Idem formatISK — un crash acá tira la página entera.
  if (n == null || !Number.isFinite(n)) return '—'
  if (n >= 1e12) return (n / 1e12).toFixed(2) + 'T'
  if (n >= 1e9) return (n / 1e9).toFixed(2) + 'B'
  if (n >= 1e6) return (n / 1e6).toFixed(2) + 'M'
  if (n >= 1e3) return (n / 1e3).toFixed(2) + 'K'
  return n.toFixed(2)
}
